import { AnimatedNumber } from "../animated";
import { cn } from "../utils";

interface CircularProgressProps {
	value: number;
	size?: number;
	strokeWidth?: number;
	showValue?: boolean;
	className?: string;
}

function clampPercentage(value: number) {
	return Math.min(Math.max(value, 0), 100);
}

export function CircularProgress({
	value,
	size = 40,
	strokeWidth = 5,
	showValue = true,
	className,
}: CircularProgressProps) {
	const percentage = clampPercentage(value);
	const radius = (size - strokeWidth) / 2;
	const circumference = 2 * Math.PI * radius;
	const dashOffset = circumference * (1 - percentage / 100);

	return (
		<div
			className={cn("relative inline-flex items-center justify-center", className)}
			style={{ width: size, height: size }}
			role="progressbar"
			aria-valuemin={0}
			aria-valuemax={100}
			aria-valuenow={percentage}
			aria-label={`${percentage}%`}
		>
			<svg
				width={size}
				height={size}
				viewBox={`0 0 ${size} ${size}`}
				className="block"
				aria-hidden="true"
			>
				<title>{`${percentage}%`}</title>
				<circle
					cx={size / 2}
					cy={size / 2}
					r={radius}
					fill="none"
					strokeWidth={strokeWidth}
					strokeDasharray={circumference}
					strokeLinecap="round"
					transform={`rotate(-90 ${size / 2} ${size / 2})`}
					className="transition-[stroke] duration-200 ease-out stroke-primary-200 dark:stroke-primary-700"
				/>
				<circle
					cx={size / 2}
					cy={size / 2}
					r={radius}
					fill="none"
					strokeWidth={strokeWidth}
					strokeDasharray={circumference}
					strokeDashoffset={dashOffset}
					strokeLinecap="round"
					transform={`rotate(-90 ${size / 2} ${size / 2})`}
					className={cn(
						"transition-[stroke-dashoffset,stroke] duration-300 ease-out",
						percentage > 0 && percentage < 50 && "stroke-red-500 dark:stroke-red-400",
						percentage >= 50 && percentage < 85 && "stroke-yellow-500 dark:stroke-yellow-400",
						percentage >= 85 && "stroke-emerald-500 dark:stroke-emerald-500",
					)}
				/>
			</svg>
			{showValue && (
				<AnimatedNumber
					className="absolute text-center text-sm font-semibold tabular-nums"
					value={percentage}
					suffix="%"
				/>
			)}
		</div>
	);
}
